// src/Components/Menu/Menu.tsx
import './Menu.scss';
import cn from 'classnames';
import {
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { List, RowComponentProps, useListRef } from 'react-window';
import { MenuRow } from './menu-constants';

// Opt-in windowing (`virtualize`) for lists longer than this; rows become a fixed 32px.
export const MENU_VIRTUALIZE_THRESHOLD = 100;
const VIRTUAL_ROW_HEIGHT = 32;
const VIRTUAL_MAX_HEIGHT = 320;

interface MenuProps<T> {
  rows: MenuRow<T>[];
  onRowClick?: (row: MenuRow<T>) => void;

  // Async / state messages
  loading?: boolean;
  loadingMessage?: ReactNode;
  errorMessage?: ReactNode;
  emptyMessage?: ReactNode;

  // Keyboard highlight (controlled by consumer via useMenuKeyboardNav)
  highlightedIndex?: number;
  onHighlightChange?: (index: number) => void;

  // a11y / styling
  listboxId?: string;
  className?: string;

  // Window long lists (> MENU_VIRTUALIZE_THRESHOLD rows); rows become a fixed height and truncate
  virtualize?: boolean;
}

const renderStateRow = (modifier: 'loading' | 'error' | 'empty', body: ReactNode): ReactElement => (
  <li
    className={cn('menu-state-row', `menu-state-row--${modifier}`)}
    aria-live={modifier === 'loading' ? 'polite' : undefined}
  >
    {body}
  </li>
);

interface MenuRowViewProps<T> {
  row: MenuRow<T>;
  index: number;
  highlightedIndex?: number;
  listboxId?: string;
  onHighlightChange?: (index: number) => void;
  onRowClick?: (row: MenuRow<T>) => void;
  style?: CSSProperties;
  virtual?: boolean;
}

const MenuRowView = <T,>({
  row,
  index,
  highlightedIndex,
  listboxId,
  onHighlightChange,
  onRowClick,
  style,
  virtual = false
}: MenuRowViewProps<T>): ReactElement => (
  <li
    id={listboxId ? `${listboxId}-option-${index}` : undefined}
    role='option'
    aria-selected={row.selected}
    aria-disabled={row.disabled}
    data-testid='menu-row'
    style={style}
    className={cn('menu-row', row.className, {
      'menu-row--selected': row.selected,
      'menu-row--disabled': row.disabled,
      'menu-row--highlighted': index === highlightedIndex,
      'menu-row--virtual': virtual
    })}
    // Only fire when the index actually changes — otherwise every mouseenter
    // re-runs the consumer's setState and re-renders the whole menu.
    onMouseEnter={() => {
      if (onHighlightChange && index !== highlightedIndex) {
        onHighlightChange(index);
      }
    }}
    onClick={() => {
      if (!row.disabled) {
        onRowClick?.(row);
      }
    }}
  >
    {row.content}
  </li>
);

interface VirtualRowProps<T> {
  highlightedIndex?: number;
  listboxId?: string;
  onHighlightChange?: (index: number) => void;
  onRowClick?: (row: MenuRow<T>) => void;
  rows: MenuRow<T>[];
}

const VirtualRow = <T,>({
  index,
  style,
  rows,
  ...rest
}: RowComponentProps<VirtualRowProps<T>>): ReactElement => (
  <MenuRowView row={rows[index]} index={index} style={style} virtual {...rest} />
);

const VirtualMenu = <T,>({
  rows,
  onRowClick,
  highlightedIndex,
  onHighlightChange,
  listboxId,
  className
}: Pick<
  MenuProps<T>,
  'rows' | 'onRowClick' | 'highlightedIndex' | 'onHighlightChange' | 'listboxId' | 'className'
>): ReactElement => {
  const listRef = useListRef(null);
  const hoverIndexRef = useRef<number | null>(null);
  const handleHighlightChange = useCallback(
    (index: number) => {
      hoverIndexRef.current = index;
      onHighlightChange?.(index);
    },
    [onHighlightChange]
  );
  const rowProps = useMemo<VirtualRowProps<T>>(
    () => ({
      highlightedIndex,
      listboxId,
      onHighlightChange: onHighlightChange && handleHighlightChange,
      onRowClick,
      rows
    }),
    [highlightedIndex, listboxId, onHighlightChange, handleHighlightChange, onRowClick, rows]
  );

  // Keep a keyboard-moved highlight in view; skip hover, or the list scrolls under the cursor.
  useEffect(() => {
    if (hoverIndexRef.current === highlightedIndex) {
      hoverIndexRef.current = null;
      return;
    }
    if (highlightedIndex != null && highlightedIndex >= 0) {
      listRef.current?.scrollToRow({ index: highlightedIndex, align: 'auto' });
    }
  }, [highlightedIndex, listRef]);

  return (
    <List
      id={listboxId}
      role='listbox'
      className={cn('menu', 'menu--virtual', className)}
      listRef={listRef}
      rowComponent={VirtualRow<T>}
      rowCount={rows.length}
      rowHeight={VIRTUAL_ROW_HEIGHT}
      rowKey={(index, props) => String(props.rows[index].value)}
      rowProps={rowProps}
      style={{ height: Math.min(rows.length * VIRTUAL_ROW_HEIGHT, VIRTUAL_MAX_HEIGHT) }}
      tagName='ul'
    />
  );
};

export const Menu = <T,>({
  rows,
  onRowClick,
  loading = false,
  loadingMessage = 'Loading…',
  errorMessage,
  emptyMessage = 'No results',
  highlightedIndex,
  onHighlightChange,
  listboxId,
  className,
  virtualize = false
}: MenuProps<T>): ReactElement => {
  // State precedence: loading > error > empty > rows. Each renders inside the same
  // <ul role='listbox'> so screen readers see a consistent landmark.
  if (loading) {
    return (
      <ul id={listboxId} role='listbox' className={cn('menu', className)}>
        {renderStateRow('loading', loadingMessage)}
      </ul>
    );
  }
  if (errorMessage != null) {
    return (
      <ul id={listboxId} role='listbox' className={cn('menu', className)}>
        {renderStateRow('error', errorMessage)}
      </ul>
    );
  }
  if (rows.length === 0) {
    return (
      <ul id={listboxId} role='listbox' className={cn('menu', className)}>
        {renderStateRow('empty', emptyMessage)}
      </ul>
    );
  }

  if (virtualize && rows.length > MENU_VIRTUALIZE_THRESHOLD) {
    return (
      <VirtualMenu
        rows={rows}
        onRowClick={onRowClick}
        highlightedIndex={highlightedIndex}
        onHighlightChange={onHighlightChange}
        listboxId={listboxId}
        className={className}
      />
    );
  }

  return (
    <ul id={listboxId} role='listbox' className={cn('menu', className)}>
      {rows.map((row, i) => (
        <MenuRowView
          key={String(row.value)}
          row={row}
          index={i}
          highlightedIndex={highlightedIndex}
          listboxId={listboxId}
          onHighlightChange={onHighlightChange}
          onRowClick={onRowClick}
        />
      ))}
    </ul>
  );
};
